import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import Autocomplete from "@mui/material/Autocomplete";
import { searchPipeline, getExportUrl, getFilterOptions } from "../services/api";
import { trackEvent } from "../services/analytics";
import type { DrugCandidate } from "../types";
import type { FilterOptions } from "../services/api";
import PhaseChip from "../components/PhaseChip";
import ProbabilityBar from "../components/ProbabilityBar";
import WACRange from "../components/WACRange";

export default function PipelineSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState<DrugCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Dynamic filter options from backend
  const [filterOpts, setFilterOpts] = useState<FilterOptions>({
    phases: [],
    therapeutic_areas: [],
    statuses: [],
    sponsors: [],
    routes: [],
    company_tiers: [],
  });

  // Initialize state from URL params (preserves filters on back navigation)
  const [phase, setPhase] = useState(searchParams.get("phase") || "");
  const [area, setArea] = useState(searchParams.get("area") || "");
  const [sponsor, setSponsor] = useState(searchParams.get("sponsor") || "");
  const [status, setStatus] = useState<string[]>(searchParams.get("status")?.split(",").filter(Boolean) || []);
  const [minProb, setMinProb] = useState(searchParams.get("minProb") || "");
  const [routeFilter, setRouteFilter] = useState<string[]>(searchParams.get("route")?.split(",").filter(Boolean) || []);
  const [tierFilter, setTierFilter] = useState(searchParams.get("tier") || "");
  const [orgType, setOrgType] = useState(searchParams.get("org_type") || "");
  const [hasAnalysis, setHasAnalysis] = useState<boolean | undefined>(
    searchParams.get("analysis") === "true" ? true : searchParams.get("analysis") === "false" ? false : undefined
  );
  const [showApproved, setShowApproved] = useState(searchParams.get("approved") === "true");

  useEffect(() => {
    getFilterOptions().then(setFilterOpts).catch(console.error);
  }, []);

  // Pagination & sorting — also from URL
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get("size") || "25"));
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "probability_of_success");
  const [sortDir, setSortDir] = useState<"asc" | "desc">((searchParams.get("dir") as "asc" | "desc") || "desc");

  // Sync state to URL params so back button works
  useEffect(() => {
    const params: Record<string, string> = {};
    if (phase) params.phase = phase;
    if (area) params.area = area;
    if (sponsor) params.sponsor = sponsor;
    if (status.length) params.status = status.join(",");
    if (minProb) params.minProb = minProb;
    if (routeFilter.length) params.route = routeFilter.join(",");
    if (tierFilter) params.tier = tierFilter;
    if (orgType) params.org_type = orgType;
    if (hasAnalysis !== undefined) params.analysis = String(hasAnalysis);
    if (showApproved) params.approved = "true";
    if (page > 0) params.page = String(page);
    if (rowsPerPage !== 25) params.size = String(rowsPerPage);
    if (sortBy !== "probability_of_success") params.sort = sortBy;
    if (sortDir !== "desc") params.dir = sortDir;
    setSearchParams(params, { replace: true });
  }, [phase, area, sponsor, status, minProb, routeFilter, tierFilter, orgType, hasAnalysis, showApproved, page, rowsPerPage, sortBy, sortDir, setSearchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await searchPipeline({
        phase: phase || undefined,
        therapeutic_area: area || undefined,
        sponsor: sponsor || undefined,
        status: status.length ? status.join(",") : undefined,
        min_probability: minProb ? parseFloat(minProb) / 100 : undefined,
        route: routeFilter.length ? routeFilter.join(",") : undefined,
        company_tier: tierFilter || undefined,
        org_type: orgType || undefined,
        has_analysis: hasAnalysis,
        show_approved: showApproved || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        page: page + 1,
        page_size: rowsPerPage,
      });
      setResults(data.results);
      setTotal(data.total);
      trackEvent("pipeline_search", { phase, area, sponsor, status, route: routeFilter, tier: tierFilter, results: data.total });
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  }, [phase, area, sponsor, status, minProb, routeFilter, tierFilter, orgType, hasAnalysis, showApproved, sortBy, sortDir, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(0);
  };

  const clearFilters = () => {
    setPhase("");
    setArea("");
    setSponsor("");
    setStatus([]);
    setRouteFilter([]);
    setHasAnalysis(undefined);
    setTierFilter("");
    setOrgType("");
    setMinProb("");
    setPage(0);
  };

  const hasFilters = phase || area || sponsor || status || minProb || routeFilter || tierFilter;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Pipeline Search</Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          href={getExportUrl({
            phase: phase || undefined,
            therapeutic_area: area || undefined,
            format: "csv",
          })}
          onClick={() => trackEvent("export_csv", { phase, area })}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Phase</InputLabel>
              <Select
                value={phase}
                label="Phase"
                onChange={(e) => { setPhase(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Phases</MenuItem>
                {filterOpts.phases.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Therapeutic Area</InputLabel>
              <Select
                value={area}
                label="Therapeutic Area"
                onChange={(e) => { setArea(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Areas</MenuItem>
                {filterOpts.therapeutic_areas.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              size="small"
              options={filterOpts.sponsors}
              value={sponsor || null}
              onChange={(_, val) => { setSponsor(val || ""); setPage(0); }}
              renderInput={(params) => (
                <TextField {...params} label="Sponsor / Manufacturer" />
              )}
              sx={{ minWidth: 250 }}
              freeSolo
              autoHighlight
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Company Tier</InputLabel>
              <Select
                value={tierFilter}
                label="Company Tier"
                onChange={(e) => { setTierFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Tiers</MenuItem>
                {(filterOpts.company_tiers || []).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t === "big_pharma" ? "Big Pharma" : t === "mid_cap" ? "Mid-Cap" : t === "emerging" ? "Emerging" : t === "startup" ? "Startup" : t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Org Type</InputLabel>
              <Select
                value={orgType}
                label="Org Type"
                onChange={(e) => { setOrgType(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="pharma">Pharma & Biotech</MenuItem>
                <MenuItem value="academic">Academic</MenuItem>
                <MenuItem value="hospital">Hospital</MenuItem>
                <MenuItem value="government">Government</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={status}
                label="Status"
                onChange={(e) => { setStatus(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value as string[]); setPage(0); }}
                renderValue={(selected) => (selected as string[]).join(", ")}
              >
                {filterOpts.statuses.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Route</InputLabel>
              <Select
                multiple
                value={routeFilter}
                label="Route"
                onChange={(e) => { setRouteFilter(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value as string[]); setPage(0); }}
                renderValue={(selected) => (selected as string[]).join(", ")}
              >
                {filterOpts.routes.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Min Probability %"
              type="number"
              value={minProb}
              onChange={(e) => { setMinProb(e.target.value); setPage(0); }}
              sx={{ width: 140 }}
              inputProps={{ min: 0, max: 100 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={hasAnalysis === true}
                  onChange={(e) => { setHasAnalysis(e.target.checked ? true : undefined); setPage(0); }}
                  size="small"
                />
              }
              label="Has AI Analysis"
              sx={{ whiteSpace: "nowrap" }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={showApproved}
                  onChange={(e) => { setShowApproved(e.target.checked); setPage(0); }}
                  size="small"
                />
              }
              label="Show approved"
              sx={{ whiteSpace: "nowrap" }}
            />

            {hasFilters && (
              <Tooltip title="Clear all filters">
                <IconButton onClick={clearFilters} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#fafafa" }}>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "drug_name"}
                        direction={sortBy === "drug_name" ? sortDir : "asc"}
                        onClick={() => handleSort("drug_name")}
                      >
                        Drug Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "sponsor"}
                        direction={sortBy === "sponsor" ? sortDir : "asc"}
                        onClick={() => handleSort("sponsor")}
                      >
                        Sponsor
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Trials</TableCell>
                    <TableCell>Therapeutic Area</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "probability_of_success"}
                        direction={sortBy === "probability_of_success" ? sortDir : "asc"}
                        onClick={() => handleSort("probability_of_success")}
                      >
                        Chance of Approval
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "projected_launch_year"}
                        direction={sortBy === "projected_launch_year" ? sortDir : "asc"}
                        onClick={() => handleSort("projected_launch_year")}
                      >
                        Est. Launch
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "projected_wac_high"}
                        direction={sortBy === "projected_wac_high" ? sortDir : "asc"}
                        onClick={() => handleSort("projected_wac_high")}
                      >
                        Projected WAC
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "estimated_market_usd_billions"}
                        direction={sortBy === "estimated_market_usd_billions" ? sortDir : "asc"}
                        onClick={() => handleSort("estimated_market_usd_billions")}
                      >
                        Market Size
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/pipeline/${row.id}`)}
                    >
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {row.drug_name}
                        </Typography>
                        {row.generic_name && row.generic_name !== row.drug_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.generic_name}
                          </Typography>
                        )}
                        {row.nct_id && (
                          <Typography variant="caption" color="text.secondary">
                            {row.nct_id}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.sponsor}</Typography>
                        {(row as any).sponsor_subsidiary && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            via {(row as any).sponsor_subsidiary}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <PhaseChip phase={row.phase} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(row as any).trial_count > 1 ? (row as any).trial_count : "--"}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.therapeutic_area || "--"}</TableCell>
                      <TableCell>
                        {row.route_detail ? (
                          <Chip label={row.route_detail} size="small" variant="outlined" />
                        ) : "--"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {row.delivery_device || "--"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          variant="outlined"
                          color={
                            row.status === "Recruiting"
                              ? "success"
                              : row.status === "Completed"
                              ? "default"
                              : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ProbabilityBar value={row.probability_of_success} />
                          {(row as any).probability_trend === "up" && (
                            <Typography variant="caption" sx={{ color: "success.main", fontWeight: 700, whiteSpace: "nowrap" }}>
                              ↑
                            </Typography>
                          )}
                          {(row as any).probability_trend === "down" && (
                            <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700, whiteSpace: "nowrap" }}>
                              ↓
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {row.projected_launch_year || "--"}
                      </TableCell>
                      <TableCell>
                        <WACRange
                          low={row.projected_wac_low}
                          high={row.projected_wac_high}
                          basis={row.pricing_basis}
                        />
                      </TableCell>
                      <TableCell>
                        {(row as any).estimated_market_usd_billions ? (
                          <Chip
                            label={`$${(row as any).estimated_market_usd_billions}B`}
                            size="small"
                            color={
                              (row as any).estimated_market_usd_billions >= 10 ? "error" :
                              (row as any).estimated_market_usd_billions >= 5 ? "warning" :
                              (row as any).estimated_market_usd_billions >= 2 ? "primary" : "default"
                            }
                            variant="outlined"
                          />
                        ) : "--"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary">
                          No candidates found. Try adjusting your filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
